// Purposes (Phase 9, ADR 0035): what the company looks people up for. Every
// lookup names one; the individual reads its description with each request.
// A company_admin registers purposes and a platform admin approves each one;
// other members see the list.
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
} from "@mantine/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage, unwrap } from "../../shared/session/errors";
import { FieldBadges, useFormat } from "../../shared/ui/format";
import { FormError, Notice } from "../../shared/ui/Page";
import { companyKey, ConfirmDialog } from "./common";
import { PURPOSE_PATTERN } from "./LookupTab";

type Purpose = Schemas["Purpose"];
type Field = Schemas["ConsentField"];

const FIELDS: Field[] = ["email", "phone", "id_number", "name"];
const STATUS_COLOUR: Record<Purpose["status"], string> = {
  pending: "yellow",
  approved: "green",
  rejected: "red",
  revoked: "gray",
};

export function PurposesTab({ companyId, admin }: { companyId: string; admin: boolean }) {
  const { t } = useTranslation();
  const f = useFormat();
  const queryClient = useQueryClient();
  const key = companyKey(companyId, "purposes");
  const purposes = useQuery({
    queryKey: key,
    queryFn: () => unwrap(api.GET("/companies/{company_id}/purposes", { params: { path: { company_id: companyId } } })),
  });
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState<Field[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Purpose | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const items = purposes.data?.items ?? [];

  const add = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    const c = code.trim();
    if (!PURPOSE_PATTERN.test(c)) return setError(t("purposes.code_invalid"));
    if (description.trim().length < 10) return setError(t("purposes.description_invalid"));
    if (fields.length === 0) return setError(t("purposes.fields_invalid"));
    setBusy(true);
    const { data, error } = await api.POST("/companies/{company_id}/purposes", {
      params: { path: { company_id: companyId } },
      body: { code: c, description: description.trim(), fields },
    });
    setBusy(false);
    if (!data) return setError(errorMessage(error, t("error.generic")));
    setCode("");
    setDescription("");
    setFields([]);
    setNotice(t("purposes.requested", { code: data.code }));
    void queryClient.invalidateQueries({ queryKey: key });
  };

  const remove = async () => {
    if (!removing) return;
    setRemoveBusy(true);
    setRemoveError(null);
    const { error, response } = await api.DELETE("/companies/{company_id}/purposes/{purpose_id}", {
      params: { path: { company_id: companyId, purpose_id: removing.id } },
    });
    setRemoveBusy(false);
    if (response.status !== 204) return setRemoveError(errorMessage(error, t("error.generic")));
    setNotice(t("purposes.removed", { code: removing.code }));
    setRemoving(null);
    void queryClient.invalidateQueries({ queryKey: key });
  };

  return (
    <Stack gap="lg">
      <Alert color="brand" title={t("purposes.rule_title")} data-testid="purposes-rule">
        {t("purposes.rule_body")}
      </Alert>
      <Paper withBorder p="md" radius="md" data-testid="purposes">
        <Stack gap="md">
          <Title order={2} size="h4">
            {t("purposes.title")}
          </Title>
          {notice && <Notice>{notice}</Notice>}
          {purposes.isPending ? (
            <Loader size="sm" aria-label={t("common.loading")} />
          ) : purposes.isError ? (
            <FormError message={errorMessage(purposes.error, t("error.generic"))} />
          ) : items.length === 0 ? (
            <Text size="sm" c="dimmed" data-testid="purposes-empty">
              {t("purposes.empty")}
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={640}>
              <Table verticalSpacing="sm" aria-label={t("purposes.title")}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("purposes.code")}</Table.Th>
                    <Table.Th>{t("purposes.description")}</Table.Th>
                    <Table.Th>{t("purposes.fields")}</Table.Th>
                    <Table.Th>{t("networks.status")}</Table.Th>
                    {admin && <Table.Th>{t("staff.actions")}</Table.Th>}
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {items.map((p) => (
                    <Table.Tr key={p.id} data-testid="purpose-row">
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {p.code}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {f.date(p.requested_at)}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{p.description}</Text>
                      </Table.Td>
                      <Table.Td>
                        <FieldBadges fields={p.fields} />
                      </Table.Td>
                      <Table.Td>
                        <Badge color={STATUS_COLOUR[p.status]} data-testid="purpose-status">
                          {t(`purposes.status_${p.status}`)}
                        </Badge>
                      </Table.Td>
                      {admin && (
                        <Table.Td>
                          {(p.status === "pending" || p.status === "approved") && (
                            <Button
                              size="xs"
                              variant="light"
                              color="red"
                              onClick={() => {
                                setRemoveError(null);
                                setRemoving(p);
                              }}
                              aria-label={t("purposes.remove_for", { code: p.code })}
                            >
                              {t("staff.remove")}
                            </Button>
                          )}
                        </Table.Td>
                      )}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Stack>
      </Paper>
      {admin && (
        <Paper withBorder p="md" radius="md">
          <form onSubmit={(e) => void add(e)} noValidate>
            <Stack gap="sm">
              <Title order={2} size="h4">
                {t("purposes.add_title")}
              </Title>
              <TextInput
                label={t("purposes.code")}
                description={t("purposes.code_hint")}
                placeholder="account_opening"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                autoCapitalize="none"
                autoComplete="off"
                spellCheck={false}
                ff="monospace"
                maw={320}
                required
              />
              <Textarea
                label={t("purposes.description")}
                description={t("purposes.description_hint")}
                value={description}
                onChange={(e) => setDescription(e.currentTarget.value)}
                maxLength={200}
                autosize
                minRows={2}
                required
              />
              <Checkbox.Group
                label={t("purposes.fields")}
                description={t("purposes.fields_hint")}
                value={fields}
                onChange={(v) => setFields(v as Field[])}
                required
              >
                <Group gap="md" mt={4}>
                  {FIELDS.map((fl) => (
                    <Checkbox key={fl} value={fl} label={f.field(fl)} />
                  ))}
                </Group>
              </Checkbox.Group>
              <FormError message={error} />
              <Button type="submit" w="fit-content" loading={busy}>
                {t("purposes.add")}
              </Button>
            </Stack>
          </form>
        </Paper>
      )}
      <ConfirmDialog
        opened={removing !== null}
        title={t("purposes.remove_title")}
        confirmLabel={t("staff.remove")}
        busy={removeBusy}
        error={removeError}
        onConfirm={() => void remove()}
        onCancel={() => setRemoving(null)}
      >
        {t("purposes.remove_body", { code: removing?.code ?? "" })}
      </ConfirmDialog>
    </Stack>
  );
}
